import crypto from 'crypto'
import { get, map, find } from 'lodash'
import { calculateTax, calculateSubtotal, calculateServiceCharge, groupTaxRules, calculateServiceFeeTaxRules } from './totalUtils'
import { invoiceNumber, nextPosOrderNumber, orderUuidFromOrderNumber } from './orderNumbers'
import { centsToDollars } from './formatters'
import { floatToCents } from '../VNUtil/VNUtils'

export const DEFAULT_EXEMPTIONS = {
  applyDiscount: true,
  applySalesTax: true,
  applyServiceFee: true,
}

// adds display attributes for fees, subtotal, and total
export const formatOrderTotals = (args = {}, useTaxClasses, taxExempt = false) => {
  const {
    discountAmountInCents,
    lineItems,
    promotions,
    serviceChargeInCents,
    taxAmountInCents,
    tipAmountInCents,
    totalAmountInCents,
  } = args

  const serviceChargeLineItem = find(lineItems, (li) => {
    return li.specialType === 'service_charge' || li.specialType === 'taxable_service_charge'
  })
  const serviceChargeTaxInCents = parseFloat(serviceChargeLineItem?.taxAmountInCents ?? 0)
  const serviceChargeDiscountInCents = parseFloat(serviceChargeLineItem?.promotions?.[0]?.discountAmountInCents ?? 0)
  const serviceChargeName = serviceChargeLineItem?.name ?? 'Service Charge'

  const safeTipCents = tipAmountInCents || 0
  const fees = discountAmountInCents + serviceChargeInCents + taxAmountInCents + safeTipCents
  const subtotalInCents = totalAmountInCents - fees - Math.abs(serviceChargeDiscountInCents)
  const promotionParamsForClient = makeClientPromotionParams(promotions)
  const taxesByExemptions = parseExemptableTaxes(lineItems)

  const taxRulesBreakdown = taxExempt ? {} : groupTaxClasses(useTaxClasses, lineItems)

  return {
    ...args,
    discountDisplay: centsToDollars(discountAmountInCents),
    promotionParamsForClient,
    serviceChargeInCents,
    serviceChargeTaxInCents,
    serviceChargeDiscountInCents,
    serviceChargeName,
    serviceChargeDisplay: centsToDollars(serviceChargeInCents),
    subtotalDisplay: centsToDollars(subtotalInCents),
    subtotalInCents,
    taxDisplay: centsToDollars(taxAmountInCents),
    totalDisplay: centsToDollars(totalAmountInCents),
    taxRulesBreakdown,
    taxExempt: taxExempt,
    ...taxesByExemptions,
  }
}

// adds display attributes for fees, subtotal, and total
export const formatOrderDetails = (args = {}) => {
  const {
    discountAmountInCents,
    lineItems,
    promotions,
    serviceChargeInCents,
    taxAmountInCents,
    tipAmountInCents,
    totalAmountInCents,
  } = args

  const serviceChargeLineItem = lineItems.find((li) => {
    return li.itemSpecialType === 'service_charge' || li.itemSpecialType === 'taxable_service_charge'
  })

  const charityLineItem = lineItems.find((li) => li.type === 'DonationLineItem')
  const serviceChargeTaxInCents = parseFloat(serviceChargeLineItem?.taxAmountInCents ?? 0)
  const serviceChargeName = serviceChargeLineItem?.name ?? 'Service Charge'

  const safeTipCents = tipAmountInCents || 0
  const fees = discountAmountInCents + serviceChargeInCents + taxAmountInCents + safeTipCents
  const subtotalInCents = totalAmountInCents - fees - floatToCents(charityLineItem?.price)
  const promotionParamsForClient = makeClientPromotionParams(promotions)
  const taxesByExemptions = parseExemptableTaxes(lineItems)

  return {
    ...args,
    discountDisplay: centsToDollars(discountAmountInCents),
    promotionParamsForClient,
    serviceChargeDisplay: centsToDollars(serviceChargeInCents),
    serviceChargeTaxInCents,
    serviceChargeName,
    subtotalDisplay: centsToDollars(subtotalInCents),
    subtotalInCents,
    taxDisplay: centsToDollars(taxAmountInCents),
    totalDisplay: centsToDollars(totalAmountInCents),
    ...taxesByExemptions,
  }
}

export const makeClientPromotionParams = (promotions = []) => {
  return map(promotions, (p) => {
    return {
      uuid: get(p, 'uuid'),
      name: get(p, 'name'),
      redemptionMethod: get(p, 'redemptionMethod'),
    }
  })
}

// remote order total might not take exemptions into consideration
// currently just need to handle tax, but could be extended if/when needed

export const parseExemptableTaxes = (lineItems = []) => {
  let exemptableTaxRules = {}
  let exemptableTaxAmountInCents = 0
  let nonExemptableTaxRules = {}
  let nonExemptableTaxAmountInCents = 0

  lineItems.forEach((lineItem) => {
    const taxRules = get(lineItem, 'taxRules') || []

    taxRules.forEach((taxRule) => {
      const uuid = get(taxRule, 'taxRuleUuid', 'unknown')
      const taxInCents = get(taxRule, 'taxAmountInCents', 0)

      const isExempt = get(taxRule, 'exempt', false)
      isExempt ? (exemptableTaxAmountInCents += taxInCents) : (nonExemptableTaxAmountInCents += taxInCents)

      let collection = isExempt ? exemptableTaxRules : nonExemptableTaxRules
      const oldRuleData = get(collection, uuid, {})
      const newRuleData = {
        ...oldRuleData,
        ...taxRule,
        lineItemCount: get(oldRuleData, 'lineItemCount', 0) + 1,
        taxAmountInCents: taxInCents + get(oldRuleData, 'taxAmountInCents', 0),
      }

      collection[uuid] = newRuleData
    })
  })

  return {
    exemptableTaxRules,
    exemptableTaxAmountInCents,
    exemptableTaxDisplay: centsToDollars(exemptableTaxAmountInCents),
    nonExemptableTaxRules,
    nonExemptableTaxAmountInCents,
    nonExemptableTaxDisplay: centsToDollars(nonExemptableTaxAmountInCents),
  }
}

const getOrderTotal = (
  subtotalAmountInCents,
  discount,
  orderInProgress,
  remoteOrderTotal,
  taxAmount,
  cartItems,
  serviceCharge = 0,
) => {
  if (subtotalAmountInCents + discount === 0) return 0

  if (orderInProgress && 'amountInCents' in orderInProgress) return orderInProgress.amountInCents
  if (orderInProgress && 'totalAmountInCents' in orderInProgress) return orderInProgress.totalAmountInCents
  if (remoteOrderTotal && 'totalAmountInCents' in remoteOrderTotal) return remoteOrderTotal?.totalAmountInCents

  return calculateSubtotal(cartItems) + taxAmount + (serviceCharge ?? 0)
}

/**
 * Calculates the cart totals and moves on - only used in CFD and POS mode
 * @param {Boolean} richCheckout a boolean depending on which button was press App or Card
 * @returns {Object} - Cart totals required for CFD to make a payment
 */
//TODO: Refactor this function
//https://airtable.com/app2mE1mTl0c4GAir/tbl4lm32cBJ1JL2IG/viwLSYXkBjOR5DQyk/rec3ljcs2QpfvJ6Xh?blocks=hide
export const calculateCartTotals = ({
  remoteOrderTotal,
  cartItems,
  taxRate,
  taxByItem,
  menuServiceCharge,
  orderInProgress,
  isFinalizingTip,
  tenderData,
  useNormalTaxRounding,
  useTaxClasses = false,
  taxClasses = {},
  taxRules = {},
  taxExempt = false,
}) => {
  const discount = orderInProgress?.discountAmountInCents || remoteOrderTotal?.discountAmountInCents || 0
  const subtotalAmountInCents =
    // TODO: [T40419] remoteOrderTotal's sutotal is `subtotalInCents`
    orderInProgress?.subtotalAmountInCents ?? remoteOrderTotal?.subtotalAmountInCents ?? calculateSubtotal(cartItems)

  const calculateTaxResult = calculateTax(
    cartItems,
    taxRate,
    taxByItem,
    useNormalTaxRounding,
    useTaxClasses,
    taxClasses,
    taxRules,
    taxExempt,
  )

  const serviceFee = {
    name: menuServiceCharge.name,
    total: calculateServiceCharge(menuServiceCharge, subtotalAmountInCents) ?? 0,
    legal: menuServiceCharge.legal,
    taxable: menuServiceCharge.taxable,
    taxClassUuid: menuServiceCharge?.taxClassUuid,
  }

  const calculateServiceFeeTaxRulesResult = calculateServiceFeeTaxRules(
    calculateTaxResult.taxRulesBreakdown ?? {},
    serviceFee,
    useNormalTaxRounding,
    taxClasses,
    taxRules,
    taxExempt,
  )

  const taxAmount =
    orderInProgress?.taxAmountInCents ??
    remoteOrderTotal?.taxAmountInCents ??
    calculateTaxResult.taxAmountInCents + calculateServiceFeeTaxRulesResult.serviceFeeTaxAmountInCents

  const taxRulesBreakdown =
    orderInProgress?.taxRulesBreakdown ??
    remoteOrderTotal?.taxRulesBreakdown ??
    calculateServiceFeeTaxRulesResult.taxRulesBreakdown ?? //the taxRulesBreakdown returned from calculateServiceFeeTaxRules already contains the order breakdown
    {}

  let serviceCharge =
    orderInProgress?.serviceChargeInCents ??
    remoteOrderTotal?.serviceChargeInCents ??
    calculateServiceCharge(menuServiceCharge, subtotalAmountInCents) ??
    0
  const orderTotal = getOrderTotal(
    subtotalAmountInCents,
    discount,
    orderInProgress,
    remoteOrderTotal,
    taxAmount,
    cartItems,
    serviceCharge,
  )
  serviceCharge = subtotalAmountInCents + discount === 0 ? 0 : serviceCharge
  const serviceChargeName =
    orderInProgress?.serviceChargeName || remoteOrderTotal?.serviceChargeName || menuServiceCharge?.name || 'Service Charge'
  const serviceChargeLegal = remoteOrderTotal?.serviceChargeLegal || menuServiceCharge?.legal

  const orderNumber = orderInProgress?.orderNumber || nextPosOrderNumber()
  const orderUuid = orderInProgress?.uuid || remoteOrderTotal?.uuid || orderUuidFromOrderNumber(orderNumber)

  let charityAmountInCents = null
  if (orderInProgress?.reopened && orderInProgress?.previousCharityAmountInCents) {
    charityAmountInCents = orderInProgress.previousCharityAmountInCents
  }

  return {
    subtotal: subtotalAmountInCents,
    discount: discount,
    tax: taxAmount,
    taxExempt,
    taxRulesBreakdown,
    useTaxClasses,
    tip: '0',
    total: orderTotal,
    uuid: orderUuid,
    serviceCharge: serviceCharge,
    serviceChargeName: serviceChargeName,
    serviceChargeLegal: serviceChargeLegal,
    orderNumber: orderNumber,
    hRef: invoiceNumber(orderNumber),
    invoiceNumber: invoiceNumber(orderNumber),
    isFinalizingTip,
    tenderData,
    reopened: orderInProgress?.reopened || false,
    charityAmountInCents,
  }
}

export const groupTaxClasses = (useTaxClasses, lineItems) => {
  const taxRulesBreakdown = {}

  if (!useTaxClasses || !lineItems?.length) {
    return taxRulesBreakdown
  }

  lineItems.forEach((lineItem) => {
    groupTaxRules(taxRulesBreakdown, lineItem)

    const modifiers = lineItem.modifiers || []

    modifiers.forEach((lineItem) => {
      groupTaxRules(taxRulesBreakdown, lineItem)
    })
  })

  return taxRulesBreakdown
}

/**
 * Generates a SHA-256 hash of the cart object
 * @param {Object} cart - The cart object to hash
 * @returns {string} - The SHA-256 hash of the cart object as a hexadecimal
 */
const hashCart = (cart) => {
  // Create a SHA-256 hash of the string
  return crypto.createHash('sha256').update(JSON.stringify(cart)).digest('hex')
}

export const cartAsSinature = (payload) => {
  const promotionIds = [...(payload.promotions?.map((promotion) => promotion.uuid || promotion) || [])].sort((a, b) => {
    return a.localeCompare(b)
  })

  const items =
    payload.items
      ?.map((item) => {
        return {
          menuItemUuid: item.menuItemUuid,
          quantity: item.quantity,
          price: item.price || item.priceInCents,
        }
      })
      .sort((a, b) => {
        return a.menuItemUuid.localeCompare(b.menuItemUuid)
      }) || []

  return hashCart({ ...payload, items, promotions: promotionIds })
}
